import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "./contexts/AuthContext";

// Route Guards
import PublicRoute from "./components/guards/PublicRoute";
import RequireAuth from "./components/guards/RequireAuth";
import RootRedirect from "./components/guards/RootRedirect";

// Layouts
import Layout from "./components/layouts/TenantLayout";
import AdminLayout from "./components/layouts/AdminLayout";

// Tenant Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import MagicLinkVerify from "./pages/MagicLinkVerify";
import Dashboard from "./pages/Dashboard";
import Campaigns from "./pages/Campaigns";
import Reports from "./pages/Reports";
import SocialAccounts from "./pages/SocialAccounts";
import OAuthCallback from "./pages/OAuthCallback";
import Settings from "./pages/Settings";
import Pricing from "./pages/Pricing";
import Billing from "./pages/Billing";
import Subscribe from "./pages/Subscribe";

// Platform Admin Pages
import AdminLogin from "./pages/auth/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import UserManagement from "./pages/admin/TenantManagement";
import RevenueDashboard from "./pages/admin/RevenueDashboard";
import SocialConnectionsAdmin from "./pages/admin/SocialConnectionsAdmin";
import CustomerSubscriptions from "./pages/admin/CustomerSubscriptions";

// Shared Components
import LoadingSpinner from "./components/LoadingSpinner";

import "./index.css";

// Google OAuth Client ID from environment
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// Placeholder components for pages not yet fully implemented
const NotFound = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-100">
    <div className="text-center">
      <div className="text-6xl mb-4">🔍</div>
      <h1 className="text-4xl font-bold text-gray-900 mb-2">404</h1>
      <p className="text-gray-600 mb-4">Page not found</p>
      <a href="/" className="text-blue-600 hover:text-blue-800 underline">
        Return to Home
      </a>
    </div>
  </div>
);

// Placeholder for System Logs if not yet implemented
const SystemLogsPlaceholder = () => (
  <div className="text-center py-20">
    <div className="text-6xl mb-4">📋</div>
    <h2 className="text-2xl font-bold text-gray-900 mb-2">System Logs</h2>
    <p className="text-gray-600">System log viewer coming soon</p>
  </div>
);

// Placeholder for Admin Settings if not yet implemented
const AdminSettingsPlaceholder = () => (
  <div className="text-center py-20">
    <div className="text-6xl mb-4">⚙️</div>
    <h2 className="text-2xl font-bold text-gray-900 mb-2">Admin Settings</h2>
    <p className="text-gray-600">Admin settings panel coming soon</p>
  </div>
);

// Placeholder for Customer Subscriptions if not yet implemented
const CustomerSubscriptionsPlaceholder = () => (
  <div className="text-center py-20">
    <div className="text-6xl mb-4">💳</div>
    <h2 className="text-2xl font-bold text-gray-900 mb-2">Customer Subscriptions</h2>
    <p className="text-gray-600">Customer subscription management coming soon</p>
  </div>
);

function App() {
  // Conditionally render GoogleOAuthProvider only if client ID is configured
  const AppContent = (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Root redirect based on authentication state and tier */}
            <Route path="/" element={<RootRedirect />} />

            {/* Public Tenant Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute tier="tenant">
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute tier="tenant">
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="/auth/verify"
              element={
                <PublicRoute tier="tenant">
                  <MagicLinkVerify />
                </PublicRoute>
              }
            />
            <Route path="/pricing" element={<Pricing />} />

              {/* Public Platform Admin Routes */}
              <Route
                path="/admin/login"
                element={
                  <PublicRoute tier="platform">
                    <AdminLogin />
                  </PublicRoute>
                }
              />

              {/* Protected Tenant Routes */}
              <Route
                path="/dashboard"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </RequireAuth>
                }
              />
              <Route
                path="/campaigns"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <Campaigns />
                    </Layout>
                  </RequireAuth>
                }
              />
              <Route
                path="/reports"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <Reports />
                    </Layout>
                  </RequireAuth>
                }
              />
              <Route
                path="/social-accounts"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <SocialAccounts />
                    </Layout>
                  </RequireAuth>
                }
              />
              <Route
                path="/oauth/callback"
                element={
                  <RequireAuth tier="tenant">
                    <OAuthCallback />
                  </RequireAuth>
                }
              />
              <Route
                path="/settings"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <Settings />
                    </Layout>
                  </RequireAuth>
                }
              />
              <Route
                path="/billing"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <Billing />
                    </Layout>
                  </RequireAuth>
                }
              />
              <Route
                path="/subscribe"
                element={
                  <RequireAuth tier="tenant">
                    <Layout>
                      <Subscribe />
                    </Layout>
                  </RequireAuth>
                }
              />

              {/* Protected Platform Admin Routes */}
              <Route
                path="/admin/dashboard"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <AdminDashboard />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <UserManagement />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/revenue"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <RevenueDashboard />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/social"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <SocialConnectionsAdmin />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/subscriptions"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <CustomerSubscriptionsPlaceholder />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/logs"
                element={
                  <RequireAuth tier="platform" role="admin">
                    <AdminLayout>
                      <SystemLogsPlaceholder />
                    </AdminLayout>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <RequireAuth tier="platform">
                    <AdminLayout>
                      <AdminSettingsPlaceholder />
                    </AdminLayout>
                  </RequireAuth>
                }
              />

              {/* Legacy admin route redirects for backward compatibility */}
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/platforms" element={<Navigate to="/admin/social" replace />} />
              <Route path="/admin/billing" element={<Navigate to="/admin/revenue" replace />} />

            {/* 404 Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );

  // Only wrap with GoogleOAuthProvider if client ID exists
  return GOOGLE_CLIENT_ID ? (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      {AppContent}
    </GoogleOAuthProvider>
  ) : (
    AppContent
  );
}

export default App;
